import { initializeApp } from "firebase/app";
import {
  getAuth,
  connectAuthEmulator,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  updateProfile,
  setPersistence,
  browserLocalPersistence,
  type User,
} from "firebase/auth";
import * as firestore from "firebase/firestore";
import { getFunctions, connectFunctionsEmulator } from "firebase/functions";
import { logger } from "../utils/logger";
import { setFirestoreClient } from "./firestoreClient";
import { FirebaseFirestoreClient } from "./FirebaseFirestoreClient";

const DEFAULT_FUNCTIONS_REGION = "northamerica-northeast1";

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

export const validateFirebaseConfig = () => {
  const required = [
    "VITE_FIREBASE_API_KEY",
    "VITE_FIREBASE_AUTH_DOMAIN",
    "VITE_FIREBASE_PROJECT_ID",
    "VITE_FIREBASE_STORAGE_BUCKET",
    "VITE_FIREBASE_MESSAGING_SENDER_ID",
    "VITE_FIREBASE_APP_ID",
  ];

  const missing = required.filter((key) => !import.meta.env[key]);
  if (missing.length > 0) {
    throw new Error(
      `Missing required Firebase environment variables: ${missing.join(", ")}`
    );
  }
};

validateFirebaseConfig();

// --- Init ---
export const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
const hasAdvancedCacheApi =
  typeof firestore.initializeFirestore === "function" &&
  typeof firestore.persistentLocalCache === "function" &&
  typeof firestore.persistentSingleTabManager === "function" &&
  typeof firestore.memoryLocalCache === "function";

// Each environment has its own Firebase project with its own default database.
// PR preview environments can use a named database within the shared preview project.
// The database ID is injected at build time via VITE_FIRESTORE_DATABASE_ID.
const firestoreDatabaseId =
  (import.meta.env.VITE_FIRESTORE_DATABASE_ID as string | undefined) ??
  undefined;

export const db = (() => {
  try {
    return hasAdvancedCacheApi
      ? firestore.initializeFirestore(
          app,
          {
            localCache:
              import.meta.env.MODE === "test"
                ? firestore.memoryLocalCache({})
                : firestore.persistentLocalCache({
                    tabManager: firestore.persistentSingleTabManager({}),
                  }),
          },
          firestoreDatabaseId
        )
      : firestoreDatabaseId
        ? firestore.getFirestore(app, firestoreDatabaseId)
        : firestore.getFirestore(app);
    /* v8 ignore start -- HMR-only fallback, untestable without Vite dev server */
  } catch {
    // During Vite HMR, initializeFirestore() may have already been called.
    // Fall back to the existing instance.
    return firestoreDatabaseId
      ? firestore.getFirestore(app, firestoreDatabaseId)
      : firestore.getFirestore(app);
  }
  /* v8 ignore stop */
})();

// Install the FirestoreClient singleton so services use the abstraction layer
setFirestoreClient(new FirebaseFirestoreClient(db));

// --- Cloud Functions ---
const functionsRegion =
  (import.meta.env.VITE_FIREBASE_FUNCTIONS_REGION as string | undefined) ??
  DEFAULT_FUNCTIONS_REGION;
export const functions = getFunctions(app, functionsRegion);

// --- Configure Auth Persistence ---
// In test mode, use browserLocalPersistence (localStorage) instead of IndexedDB
// This allows Playwright's storageState to capture and restore the auth state
if (import.meta.env.MODE === "test") {
  // Note: setPersistence is best-effort configuration, not a critical Firestore operation
  // If it fails, auth will still work but might use default persistence
  // We log the error but don't block initialization
  /* v8 ignore start -- non-deterministic runtime branch; exercised in emulator integration flows */
  setPersistence(auth, browserLocalPersistence).catch((error) => {
    logger.error("Failed to set auth persistence in test mode", {
      error,
      mode: import.meta.env.MODE,
      persistenceType: "browserLocalPersistence",
      impact: "Auth will use default persistence instead",
    });
  });
  /* v8 ignore stop */
}

// --- Emulators ---
// CRITICAL: In test mode, ALWAYS use Firebase emulator to prevent accidental production usage
//
// Ports are configurable via VITE_FIREBASE_AUTH_EMULATOR_PORT /
// VITE_FIREBASE_FIRESTORE_EMULATOR_PORT so E2E tests can run on isolated
// ports (discovered at runtime by scripts/find-e2e-ports.sh and
// tests/e2e/config/ports.ts) that never conflict with a locally-running
// dev emulator.
/* v8 ignore start -- emulator connection failures and alternate runtime branches are covered in E2E/CI */
const authEmulatorPort =
  Number(import.meta.env.VITE_FIREBASE_AUTH_EMULATOR_PORT) || 9099;
const firestoreEmulatorPort =
  Number(import.meta.env.VITE_FIREBASE_FIRESTORE_EMULATOR_PORT) || 8080;
const functionsEmulatorPort =
  Number(import.meta.env.VITE_FIREBASE_FUNCTIONS_EMULATOR_PORT) || 5001;

if (import.meta.env.MODE === "test") {
  // Hard-fail if emulator is not configured in test mode
  if (import.meta.env.VITE_USE_FIREBASE_EMULATOR !== "true") {
    throw new Error(
      "🚨 CRITICAL: Firebase emulator not configured in test mode!\n" +
        "Tests must NEVER run against production Firebase.\n" +
        'Set VITE_USE_FIREBASE_EMULATOR="true" in .env.test or environment variables.'
    );
  }

  // Explicitly connect to emulators with validation
  try {
    connectAuthEmulator(auth, `http://localhost:${authEmulatorPort}`, {
      disableWarnings: true,
    });
    firestore.connectFirestoreEmulator(db, "localhost", firestoreEmulatorPort);
    connectFunctionsEmulator(functions, "localhost", functionsEmulatorPort);
  } catch (error) {
    // If already connected, that's fine (prevents multiple connection errors)
    // But if it's a real error, we should know about it
    if (
      error instanceof Error &&
      !error.message.includes("already been called")
    ) {
      throw new Error(
        "🚨 Failed to connect to Firebase emulators.\n" +
          "Ensure emulators are running: npm run emulator\n" +
          `Original error: ${error.message}`
      );
    }
  }
} else if (
  import.meta.env.DEV &&
  import.meta.env.VITE_USE_FIREBASE_EMULATOR === "true"
) {
  // Development mode with emulator (optional) — uses default ports
  connectAuthEmulator(auth, `http://localhost:${authEmulatorPort}`, {
    disableWarnings: true,
  });
  firestore.connectFirestoreEmulator(db, "localhost", firestoreEmulatorPort);
  connectFunctionsEmulator(functions, "localhost", functionsEmulatorPort);
}
/* v8 ignore stop */

// --- TEST HOOK (Playwright) ---
// Expose Firebase SDK functions on window for E2E tests.
// page.evaluate() runs in the browser where bare module specifiers
// (e.g. import('firebase/auth')) cannot be resolved, so tests access
// Firebase through these window globals instead.
if (import.meta.env.MODE === "test") {
  // @ts-expect-error – exposed intentionally for E2E tests
  window.__FIREBASE_AUTH__ = {
    ...auth,
    /* v8 ignore start -- wrappers exercised in browser E2E via window globals */
    signInWithEmailAndPassword: (email: string, password: string) =>
      signInWithEmailAndPassword(auth, email, password),
    createUserWithEmailAndPassword: (email: string, password: string) =>
      createUserWithEmailAndPassword(auth, email, password),
    updateProfile: (
      user: User,
      profile: { displayName?: string | null; photoURL?: string | null }
    ) => updateProfile(user, profile),
    /* v8 ignore stop */
  };

  // @ts-expect-error – exposed intentionally for E2E tests
  window.__FIREBASE_DB__ = {
    db,
    doc: firestore.doc,
    getDoc: firestore.getDoc,
    setDoc: firestore.setDoc,
    deleteDoc: firestore.deleteDoc,
    serverTimestamp: firestore.serverTimestamp,
    Timestamp: firestore.Timestamp,
  };
}
